import React, { useEffect, useMemo, useState } from 'react';
import { X, Pencil } from 'lucide-react';
import { esPeso, cantidadLegible } from '../../../lib/helpers/products';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';
import Button from '../../Elements/Button';

/**
 * Editar una comanda viva desde Barra (plan CRUO 24-ago, bloque C).
 *
 * Es la salida del callejón «4 cervezas pedidas, 3 en stock»: esa comanda no se
 * podía servir ni corregir. Aquí se cambian cantidades, se quitan líneas y se
 * mueve (o quita) la mesa; el guardado va entero en un PATCH — o llega todo, o
 * no llega nada.
 *
 * Solo desde Barra a propósito: en el carrito de Sala ya se edita antes de
 * enviar. Y no se añaden productos — eso obligaría a meter la carta entera aquí.
 *
 * El molde es el de los modales de esta pantalla (BasculaModal): overlay fijo,
 * cierre con × o fondo, foco en lo que se toca.
 */
export default function EditarComandaModal({ active, order, mesas, busy, onClose, onSave }) {
  // Cantidad deseada por línea (string: es un input) y líneas marcadas para quitar.
  const [cantidades, setCantidades] = useState({});
  const [quitadas, setQuitadas] = useState(() => new Set());
  const [mesaId, setMesaId] = useState('');
  const overlayDismiss = useOverlayDismiss(onClose);

  useEffect(() => {
    if (!active || !order) return;
    const iniciales = {};
    for (const it of order.items || []) iniciales[it.id] = String(Number(it.quantity));
    setCantidades(iniciales);
    setQuitadas(new Set());
    setMesaId(order.table_id || '');
  }, [active, order]);

  const lineas = useMemo(() => {
    return (order?.items || []).map((it) => {
      const peso = esPeso(it);
      const bruto = cantidades[it.id];
      const num = bruto === undefined || bruto === '' ? null : Number(bruto);
      const valida =
        num != null && Number.isFinite(num) && num > 0 && (peso || Number.isInteger(num));
      const cambiada = valida && num !== Number(it.quantity);
      return { it, peso, bruto: bruto ?? '', num, valida, cambiada, quitada: quitadas.has(it.id) };
    });
  }, [order, cantidades, quitadas]);

  if (!active || !order) return null;

  const vivas = lineas.filter((l) => !l.quitada);
  // Quitar TODAS no es editar, es cancelar — y ese botón ya existe con su confirmación.
  const todoValido = vivas.length > 0 && vivas.every((l) => l.valida);
  const algunPesoCambiado = vivas.some((l) => l.peso && l.cambiada && l.it.real_weight != null);

  const setCantidad = (id, valor) => setCantidades((c) => ({ ...c, [id]: valor }));
  const paso = (linea, delta) => {
    const actual = linea.num ?? Number(linea.it.quantity) ?? 1;
    setCantidad(linea.it.id, String(Math.max(1, Math.round(actual) + delta)));
  };
  const alternarQuitar = (id) =>
    setQuitadas((q) => {
      const s = new Set(q);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  const guardar = () => {
    if (!todoValido || busy) return;
    onSave({
      items: vivas.map((l) => ({ order_item_id: l.it.id, quantity: l.num })),
      // Siempre explícita: la pantalla enseña el valor actual precargado, así que
      // lo que se guarda es exactamente lo que se ve (el backend no toca la mesa
      // si coincide con la que ya tenía).
      table_id: mesaId || null,
      updated_at: order.updated_at,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-auto bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Editar comanda"
      {...overlayDismiss}
    >
      <div className="flex w-full max-w-[420px] flex-col gap-4 rounded-lg border border-[var(--border)] bg-[var(--background)] p-6">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 className="flex items-center gap-2 text-lg font-medium text-[var(--foreground)]">
              <Pencil size={18} strokeWidth={1.5} aria-hidden /> Editar comanda
            </h2>
            <p className="text-sm text-[var(--muted-foreground)]">
              {order.customer_name || 'Socio'} · #{String(order.id).slice(0, 8)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="-m-1 flex-shrink-0 rounded-sm p-1 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
          >
            <X size={16} strokeWidth={1.5} aria-hidden />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {lineas.map((l) => (
            <div
              key={l.it.id}
              className={`rounded-sm border px-3 py-2.5 ${
                l.quitada ? 'border-dashed border-[var(--border)] opacity-50' : 'border-[var(--border)]'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`min-w-0 break-words text-sm font-medium text-[var(--foreground)] ${
                    l.quitada ? 'line-through' : ''
                  }`}
                >
                  {l.it.product_name}
                </span>
                <button
                  type="button"
                  onClick={() => alternarQuitar(l.it.id)}
                  className={`flex-shrink-0 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                    l.quitada
                      ? 'text-[var(--foreground)] hover:bg-[var(--muted)]'
                      : 'text-[var(--destructive)] hover:bg-[var(--destructive-bg)]'
                  }`}
                >
                  {l.quitada ? 'Deshacer' : 'Quitar'}
                </button>
              </div>
              {!l.quitada && (
                <div className="mt-2 flex items-center gap-2">
                  {l.peso ? (
                    <label className="flex items-center gap-2 text-xs font-medium text-[var(--muted-foreground)]">
                      Gramos
                      <input
                        type="number"
                        step="0.001"
                        min="0.001"
                        inputMode="decimal"
                        value={l.bruto}
                        onChange={(e) => setCantidad(l.it.id, e.target.value)}
                        aria-label={`Gramos de ${l.it.product_name}`}
                        className={`h-9 w-28 rounded-md border px-2 text-sm tabular-nums ${
                          l.valida ? 'border-[var(--border)]' : 'border-[var(--destructive)]'
                        }`}
                      />
                    </label>
                  ) : (
                    <div className="flex items-center gap-1">
                      {/* Botones de 36px: esta pantalla se toca con el dedo. */}
                      <button
                        type="button"
                        onClick={() => paso(l, -1)}
                        aria-label={`Una menos de ${l.it.product_name}`}
                        className="h-9 w-9 rounded-md border border-[var(--border)] text-base font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        inputMode="numeric"
                        value={l.bruto}
                        onChange={(e) => setCantidad(l.it.id, e.target.value)}
                        aria-label={`Cantidad de ${l.it.product_name}`}
                        className={`h-9 w-14 rounded-md border px-1 text-center text-sm tabular-nums ${
                          l.valida ? 'border-[var(--border)]' : 'border-[var(--destructive)]'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => paso(l, 1)}
                        aria-label={`Una más de ${l.it.product_name}`}
                        className="h-9 w-9 rounded-md border border-[var(--border)] text-base font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]"
                      >
                        +
                      </button>
                    </div>
                  )}
                  <span className="ml-auto text-xs tabular-nums text-[var(--muted-foreground)]">
                    antes: {cantidadLegible(l.it.quantity)}
                    {l.peso ? ' g' : '×'}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        <label className="text-sm text-[var(--foreground)]">
          <span className="mb-1 block font-medium">Entrega</span>
          <select
            value={mesaId}
            onChange={(e) => setMesaId(e.target.value)}
            className="h-9 w-full rounded-md border border-[var(--border)] bg-white px-2 text-sm text-[var(--foreground)]"
          >
            <option value="">🛎️ Recoge en barra</option>
            {(mesas || []).map((m) => (
              <option key={m.id} value={m.id}>
                🍽️ {m.label}
                {m.zone ? ` · ${m.zone}` : ''}
              </option>
            ))}
          </select>
        </label>

        {algunPesoCambiado && (
          <p className="rounded-md bg-[var(--toast-warning-bg)] px-3 py-2 text-xs text-[var(--toast-warning-text)]">
            Has cambiado los gramos de una línea ya pesada: al servir se volverá a pedir la
            báscula, porque el peso apuntado era de la cantidad anterior.
          </p>
        )}
        {vivas.length === 0 && (
          <p className="rounded-md bg-[var(--toast-error-bg)] px-3 py-2 text-xs text-[var(--toast-error-text)]">
            Una comanda no puede quedarse sin líneas: para eliminarla entera, usa Cancelar.
          </p>
        )}

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Volver
          </Button>
          <Button className="flex-1" disabled={!todoValido || busy} onClick={guardar}>
            Guardar cambios
          </Button>
        </div>
      </div>
    </div>
  );
}
